import { Eye } from "lucide-react";
import { Temporal } from "temporal-polyfill";
import { TimeZone } from "@api/shared-runtime/timezone";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatCredentialTimestamp, formatDate } from "../../lib/format-date";
import { VerificationSourceLink } from "../verification-source-link";
import {
  groupDisciplinaryEvidence,
  disciplinaryEvidenceLabel,
} from "./disciplinary-evidence";
import type { DisciplinaryAssessment, VerificationCheck } from "./types";

type Evidence = VerificationCheck["evidenceManifest"][number];
type OpenEvidence = (target: { fileUploadId: string; label: string }) => void;
/** What the board did; shown with each record. */
const ACTION_FIELDS = {
  action: "Source action",
  misconduct: "Source misconduct description",
  conditions: "Conditions",
} as const;
/** How the record was matched to the provider; kept one click away. */
const MATCHING_FIELDS = {
  licenseNumber: "License number",
  licenseType: "License type",
  birthYear: "Birth year",
  effectiveDate: "Effective date",
  updatedDate: "Updated date",
} as const;
const DATE_FIELDS = new Set(["effectiveDate", "updatedDate"]);

export function DisciplinaryDetails({
  assessment,
  evidence,
  onOpenEvidence,
}: {
  assessment: DisciplinaryAssessment;
  evidence: Evidence[];
  onOpenEvidence: OpenEvidence;
}) {
  const { records, sourceEvidence } = groupDisciplinaryEvidence({
    assessment,
    evidence,
  });
  const dataset = assessment.dataset;
  return (
    <section
      aria-label="Public board records"
      className="space-y-3 border-t pt-3 text-xs"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-1">
          <h5 className="text-sm font-semibold">Public board records</h5>
          <p className="text-muted-foreground">
            {assessment.sourceName} · Checked{" "}
            {formatCredentialTimestamp(assessment.observedAt)}
          </p>
        </div>
        <VerificationSourceLink
          sourceUrl={dataset?.sourceUrl ?? assessment.sourceUrl}
        />
      </div>
      {/* A clear result is the answer; anything else is listed with the
          issues at the top, so the source's scope stays collapsed here. */}
      {assessment.status === "clear" && (
        <p className="font-medium text-green-800">{assessment.label}</p>
      )}
      <details className="space-y-2">
        <summary className="cursor-pointer text-muted-foreground">
          About this source
        </summary>
        <div className="space-y-2 pt-1">
          {assessment.status !== "clear" && (
            <p className="font-medium">{assessment.label}</p>
          )}
          <p className="text-muted-foreground">{assessment.description}</p>
          <p className="text-muted-foreground">{assessment.scope}</p>
          {dataset && (
            <dl>
              <div className="space-y-1">
                <dt className="text-muted-foreground">Dataset published</dt>
                <dd>
                  {formatDate(
                    Temporal.Instant.from(dataset.publishedAt)
                      .toZonedDateTimeISO(TimeZone.AmericaNewYork)
                      .toPlainDate()
                      .toString(),
                  )}
                </dd>
              </div>
            </dl>
          )}
          {assessment.coverageNote && (
            <p className="text-muted-foreground">{assessment.coverageNote}</p>
          )}
        </div>
      </details>
      {sourceEvidence.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {sourceEvidence.map((file, index) => (
            <DisciplinaryEvidenceButton
              key={file.fileUploadId}
              evidence={file}
              label={disciplinaryEvidenceLabel(file, index)}
              onOpen={onOpenEvidence}
            />
          ))}
        </div>
      )}
      {records.map(({ record, captures, orders }) => {
        const name = [record.firstName, record.middleName, record.lastName]
          .filter(Boolean)
          .join(" ");
        return (
          <article
            key={record.actionId}
            aria-label={`${record.label}${name ? ` · ${name}` : ""}`}
            className="space-y-3 border-t pt-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="space-y-1">
                <h6 className="font-semibold">{record.label}</h6>
                {record.identityMatch !== "matched" && (
                  <p className="font-medium text-amber-900">
                    {record.identityLabel}
                  </p>
                )}
              </div>
              <VerificationSourceLink sourceUrl={record.sourceUrl} />
            </div>
            <RecordFields record={record} fields={ACTION_FIELDS} />
            <details className="space-y-2">
              <summary className="cursor-pointer text-muted-foreground">
                Matching details
              </summary>
              <div className="space-y-2 pt-1">
                {name && <p className="font-medium">{name}</p>}
                {record.identityMatch === "matched" && (
                  <p className="font-medium">{record.identityLabel}</p>
                )}
                <p className="text-muted-foreground">{record.matchingReason}</p>
                <RecordFields record={record} fields={MATCHING_FIELDS} />
              </div>
            </details>
            {!record.detailsComplete && (
              <p className="text-amber-900">
                Full record details were not available. The source candidate is
                retained for review.
              </p>
            )}
            <Limitations items={record.limitations} />
            {captures.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {captures.map((file, index) => (
                  <DisciplinaryEvidenceButton
                    key={file.fileUploadId}
                    evidence={file}
                    label={disciplinaryEvidenceLabel(file, index)}
                    onOpen={onOpenEvidence}
                  />
                ))}
              </div>
            )}
            {orders.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium">Board orders</p>
                {orders.map(
                  ({ order, evidence: file, alreadyShown }, index) => (
                    <div
                      key={`${order.sourceUrl}-${index}`}
                      className="flex flex-wrap items-center gap-2"
                    >
                      {file && !alreadyShown && (
                        <DisciplinaryEvidenceButton
                          evidence={file}
                          label={`Board order ${index + 1} (PDF)`}
                          onOpen={onOpenEvidence}
                        />
                      )}
                      {alreadyShown && (
                        <span className="text-muted-foreground">
                          Retained order shown with another record above.
                        </span>
                      )}
                      {!file && (
                        <span className="text-muted-foreground">
                          No retained PDF is available for this order.
                        </span>
                      )}
                      <VerificationSourceLink sourceUrl={order.sourceUrl} />
                    </div>
                  ),
                )}
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
}

type BoardRecord = DisciplinaryAssessment["records"][number];

function RecordFields({
  record,
  fields,
}: {
  record: BoardRecord;
  fields: Partial<Record<keyof BoardRecord, string>>;
}) {
  const entries = Object.entries(fields).flatMap(([key, label]) => {
    const value = record[key as keyof BoardRecord];
    if (value === null || value === undefined || value === "") {
      return [];
    }
    const text =
      DATE_FIELDS.has(key) && typeof value === "string"
        ? formatDate(value)
        : String(value);
    return [{ key, label, text }];
  });
  if (!entries.length) {
    return null;
  }
  return (
    <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
      {entries.map(({ key, label, text }) => (
        <div
          key={key}
          className={cn(
            "min-w-0 space-y-1",
            key in ACTION_FIELDS && "sm:col-span-2",
          )}
        >
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="whitespace-pre-wrap break-words">{text}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A single record's limitations; the search-wide ones are listed with the
 * issues at the top of the result. */
function Limitations({ items }: { items: string[] }) {
  if (!items.length) {
    return null;
  }
  return (
    <ul className="list-disc space-y-1 pl-4 text-amber-900">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function DisciplinaryEvidenceButton({
  evidence,
  label,
  onOpen,
}: {
  evidence: Evidence;
  label: string;
  onOpen: OpenEvidence;
}) {
  return (
    <Button
      type="button"
      variant="surface"
      size="sm"
      className="gap-1.5"
      onClick={() => onOpen({ fileUploadId: evidence.fileUploadId, label })}
    >
      <Eye className="size-4" aria-hidden="true" />
      {label}
    </Button>
  );
}
